import React from 'react';

import { isEmpty } from '@nfctron/nexus-core';
import { UiButton, UiDrawer, UiDrawerContent, UiDrawerHeader, UiDrawerTitle, UiDrawerTrigger, cn } from '@nfctron/nexus-react';

import { Text } from '@mantine/core';
import { Scanner } from '@yudiel/react-qr-scanner';
import { CameraIcon } from 'lucide-react';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type Component } from '@root/lib/types/components';

import { type QrCodeReaderButtonProps } from './QrCodeReaderButton.types';

/**
 * QrCodeReaderButton component
 * @author neumie <neumie@neumie.dev>
 * @constructor
 */
export const QrCodeReaderButton: Component<QrCodeReaderButtonProps> = (props) => {
	const { className, onScanned, ...rest } = props;
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON, QrCodeReaderButton.displayName);
	const [isOpened, setIsOpened] = React.useState(false);

	return (
		<UiDrawer open={isOpened} onClose={() => setIsOpened(false)}>
			<UiDrawerTrigger asChild>
				<UiButton className={cn('', className)} rightSection={<CameraIcon />} {...rest} />
			</UiDrawerTrigger>
			<UiDrawerContent className="mx-auto max-w-screen-sm">
				<UiDrawerHeader className="flex justify-center border-b border-zinc-200 py-6">
					<UiDrawerTitle asChild>
						<Text fw={700} c="lucky-point" fz={18}>
							{t('title')}
						</Text>
					</UiDrawerTitle>
				</UiDrawerHeader>

				{/* content */}
				<div className="flex items-center justify-center p-4">
					{/* scanner */}
					<div className="w-full max-w-full rounded-xl">
						<Scanner
							classNames={{
								container: '[&>div>div]:w-full [&>div>div]:h-full [&>div>div>svg]:h-full [&>div>div>svg]:w-full',
							}}
							onScan={(result) => {
								const qrCode = !isEmpty(result[0]?.rawValue) ? result[0]!.rawValue : '';
								onScanned({ qrCode });
								setIsOpened(false);
							}}
						/>
					</div>
				</div>
			</UiDrawerContent>
		</UiDrawer>
	);
};

/**
 * QrCodeReaderButton component display name
 * @type {ComponentDisplayName}
 */
QrCodeReaderButton.displayName = 'component/QrCodeReaderButton';
